import { useAuth } from "@/context/auth-context";
import { useDashboard } from "@/lib/queries";
import type { UserScoreExtended } from "@/types/extended-types";
import Challenges from "@/components/dashboard/Challenges";
import LoadingSpinner from "@/components/LoadingSpinner";
import Achievements from "@/components/dashboard/Achievements";
import Greeting from "@/components/dashboard/Greeting";
// import Sidebar from "@/components/dashboard/SideBar";
import Charts from "@/components/dashboard/Charts";
import StreakStars from "@/components/dashboard/StreakStars";
import AllTimeStars from "@/components/dashboard/AllTimeStars";
import { Link } from "@tanstack/react-router";

export const calculateStreak = (userScores: UserScoreExtended[]) => {
  const today = new Date();
  let streak = 0;

  for (let i = 0; i < userScores.length; i++) {
    const scoreDate = new Date(userScores[i].created);
    const diffTime = Math.abs(today.getTime() - scoreDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === streak) {
      streak++;
    } else if (diffDays > streak) {
      break;
    }
  }

  return streak;
};

const DashboardPage = () => {
  const { user, isLoading } = useAuth();

  // Single dashboard query
  const { data: dashboardData, isLoading: loadingDashboard } = useDashboard(
    user?.id
  );

  // Loading state
  if (loadingDashboard || isLoading) {
    return (
      <LoadingSpinner text="Loading dashboard..." className="min-h-screen" />
    );
  }

  if (!dashboardData) {
    return <div>Error loading dashboard</div>;
  }

  // Extract data from dashboard response
  const { global, user: userData } = dashboardData;

  // Format data for components
  const streakStarsArray = (global.streakLeaders || []).map((leader) => ({
    id: leader.id,
    number: leader.streak,
    name: leader.username || leader.fullName,
    avatar: leader.avatar,
  }));

  const allTimeStarsArray = (global.allTimeLeaders || []).map((leader) => ({
    id: leader.id,
    name: leader.username || leader.fullName,
    starRating: Math.floor(leader.score / 100), // Simple calculation
  }));

  const quizChampionsArray = (global.quizChampions || [])
    .slice(0, 5)
    .map((champion) => ({
      name: champion.username || champion.fullName,
      quizzes: champion.totalQuizzes,
    }));

  const weeklyLeadersArray = (global.weeklyLeaders || [])
    .slice(0, 5)
    .map((leader) => ({
      fullName: leader.username || leader.fullName,
      username: leader.username,
      score: leader.score,
    }));

  return (
    <div className="pt-24 dashboard-container container m-auto px-4 lg:px-0">
      <Greeting
        name={user?.name || user?.username || "User"}
        readingPower={user?.reading_power || 0}
        gender={user?.gender}
        avatar={user?.avatar}
        userId={user?.id}
      />
      
      {/* Quiz Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center my-8 max-w-4xl mx-auto">
        <Link
          to="/books/search"
          className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-6 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-center text-xl"
        >
          📚 Do a Book Quiz
        </Link>
        <Link
          to="/articles"
          className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-6 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-center text-xl"
        >
          📰 Do a News Quiz
        </Link>
      </div>

      <div className="dashboard-main-grid">
        <div className="dashboard-col-1">
          <Achievements
            booksNumber={userData.achievements.booksRead}
            quizzesNumber={userData.achievements.quizzesTaken}
            averageScore={userData.achievements.averageScore}
            longestStreak={userData.achievements.longestStreak}
            currentLevel={userData.achievements.currentLevel}
          />
          <Challenges
            donePercentageDaily={userData.challenges.daily.scorePercentage}
            notdonePercentageDaily={
              100 - userData.challenges.daily.scorePercentage
            }
            donePercentageMonthly={userData.challenges.monthly.scorePercentage}
            notdonePercentageMonthly={
              100 - userData.challenges.monthly.scorePercentage
            }
            challengesData={userData.challenges}
          />
          <Charts
            quizChampions={quizChampionsArray}
            weeklyLeaders={weeklyLeadersArray}
          />
          <div className="dashboard-leaderboard mt-6 mb-12 space-y-6">
            <StreakStars starsArray={streakStarsArray} />
            <AllTimeStars users={allTimeStarsArray} />
          </div>
        </div>
        {/* <div>{user && <Sidebar />}</div> */}
      </div>
    </div>
  );
};

export default DashboardPage;
